/**
 * Frontend init for the Testimonials Elementor widget — plain Widget_Base
 * (like Products), so this is ONE unconditional script working correctly
 * in both the editor preview iframe and the live page; no isEditMode()
 * branching needed (see elementor-products.js for the same reasoning).
 *
 * Two responsibilities per widget instance:
 * 1. Initialize the carousel via the shared elementor-swiper-carousel.js
 *    (same as Slider/Products).
 * 2. Wire up "Read More" -> modal. Each testimonial's full modal content
 *    (author/jobTitle/photo/content) used to be pre-built and JSON-embedded
 *    for EVERY testimonial on EVERY page load - on real imported content
 *    (68 Divi-shortcode-laden testimonials) that was measured pushing
 *    initial page memory over 1GB. Now fetched ON DEMAND instead, via
 *    register.php's `gws/v1/testimonial-content` REST route, only for
 *    whichever single testimonial "Read More" is actually clicked for -
 *    see that file's own docblock for the full explanation and the two
 *    lookup modes (post-type vs. manual repeater row).
 *
 *    Pauses the carousel's autoplay while the modal is open, resumes on
 *    close, per explicit user direction. A single shared modal element per
 *    widget instance (not one per slide) is populated/toggled here rather
 *    than rendered N times server-side.
 */
( function () {
	'use strict';

	function getClosestDataElementorId( element ) {
		var closest = element && element.closest( '[data-elementor-id]' );
		return closest ? closest.getAttribute( 'data-elementor-id' ) : null;
	}

	function testimonialsConfig() {
		return window.gwsTestimonialsConfig || null;
	}

	/**
	 * @param {Element} modal
	 * @param {Object}  payload
	 */
	function populateModal( modal, payload ) {
		var photoWrap = modal.querySelector( '.gws-testimonial-modal__photo-wrap' );
		var contentEl = modal.querySelector( '.gws-testimonial-modal__content' );
		var nameEl = modal.querySelector( '.gws-testimonial-modal__author-name' );
		var titleEl = modal.querySelector( '.gws-testimonial-modal__author-title' );

		photoWrap.innerHTML = payload.photoUrl
			? '<img src="' + payload.photoUrl + '" alt="' + ( payload.photoAlt || '' ).replace( /"/g, '&quot;' ) + '" loading="lazy" />'
			: '';

		// content is already-filtered/escaped HTML from PHP
		// (wpautop(do_shortcode(...)), see register.php) - safe to set as
		// innerHTML, same trust boundary as the card preview's own
		// wp_kses_post() output.
		contentEl.innerHTML = payload.content || '';
		nameEl.textContent = payload.author || '';
		titleEl.textContent = payload.jobTitle || '';
		titleEl.style.display = payload.jobTitle ? '' : 'none';
	}

	/**
	 * Builds the REST query string for one "Read More" trigger's testimonial
	 * - 'post' mode only needs the real testimonial post ID; 'manual' mode
	 * has no independent post to query, so it needs this widget instance's
	 * own element ID (scopeEl's data-gws-testimonials-widget-id) plus the
	 * connected page/document's own ID (the SAME closest-data-elementor-id
	 * lookup the Product Filters widget uses, for the same reason - this
	 * widget might live inside a Theme Builder template, a different
	 * document than "the current page").
	 *
	 * @param {Element} trigger
	 * @param {Element} scopeEl
	 *
	 * @return {string|null} Query string (no leading '?'), or null if a
	 * required piece is missing.
	 */
	function buildContentQuery( trigger, scopeEl ) {
		var mode = trigger.getAttribute( 'data-gws-testimonial-mode' );
		var params = new URLSearchParams();
		params.set( 'mode', mode );

		if ( 'post' === mode ) {
			var testimonialId = trigger.getAttribute( 'data-gws-testimonial-id' );

			if ( ! testimonialId ) {
				return null;
			}

			params.set( 'testimonial_id', testimonialId );
		} else {
			var widgetId = scopeEl.getAttribute( 'data-gws-testimonials-widget-id' );
			var itemIndex = trigger.getAttribute( 'data-gws-testimonial-item-index' );
			var postId = getClosestDataElementorId( scopeEl ) || ( window.elementorFrontend.config.post && window.elementorFrontend.config.post.id );

			if ( ! widgetId || null === itemIndex || ! postId ) {
				return null;
			}

			params.set( 'widget_id', widgetId );
			params.set( 'item_index', itemIndex );
			params.set( 'post_id', postId );
		}

		return params.toString();
	}

	/**
	 * @param {Element}     scopeEl
	 * @param {Object|null} swiperInstance
	 */
	function initModal( scopeEl, swiperInstance ) {
		var modal = scopeEl.querySelector( '.gws-testimonial-modal' );

		if ( ! modal ) {
			return;
		}

		// Cached per query string within this page view only - re-opening
		// the same testimonial's modal shouldn't re-fetch, but nothing
		// here needs to survive a reload.
		var contentCache = {};

		function openModal( payload ) {
			populateModal( modal, payload );
			modal.hidden = false;
			// rAF: let `hidden` removal paint first so the CSS transition
			// (opacity/transform on .is-open) actually animates in.
			window.requestAnimationFrame( function () {
				modal.classList.add( 'is-open' );
			} );

			if ( swiperInstance && swiperInstance.autoplay ) {
				swiperInstance.autoplay.stop();
			}
		}

		function closeModal() {
			modal.classList.remove( 'is-open' );
			modal.hidden = true;

			if ( swiperInstance && swiperInstance.autoplay && swiperInstance.params.autoplay ) {
				swiperInstance.autoplay.start();
			}
		}

		function handleReadMoreClick( trigger ) {
			var config = testimonialsConfig();

			if ( ! config || trigger.classList.contains( 'is-loading' ) ) {
				return;
			}

			var query = buildContentQuery( trigger, scopeEl );

			if ( ! query ) {
				return;
			}

			if ( contentCache[ query ] ) {
				openModal( contentCache[ query ] );
				return;
			}

			trigger.classList.add( 'is-loading' );

			fetch( config.restUrl + 'testimonial-content?' + query, {
				headers: { 'X-WP-Nonce': config.nonce },
			} )
				.then( function ( response ) { return response.ok ? response.json() : null; } )
				.catch( function () { return null; } )
				.then( function ( payload ) {
					trigger.classList.remove( 'is-loading' );

					if ( ! payload || ! payload.content ) {
						return;
					}

					contentCache[ query ] = payload;
					openModal( payload );
				} );
		}

		scopeEl.addEventListener( 'click', function ( event ) {
			var trigger = event.target.closest( '[data-gws-testimonial-mode]' );

			if ( trigger ) {
				handleReadMoreClick( trigger );
				return;
			}

			if ( event.target.closest( '[data-gws-testimonial-modal-close]' ) ) {
				closeModal();
			}
		} );

		document.addEventListener( 'keydown', function ( event ) {
			if ( 'Escape' === event.key && ! modal.hidden ) {
				closeModal();
			}
		} );
	}

	function registerElementReadyHook() {
		window.elementorFrontend.hooks.addAction( 'frontend/element_ready/gws-testimonials.default', function ( $scope ) {
			var scopeEl = $scope[ 0 ];
			var carousel = scopeEl.querySelector( '.swiper' );
			var swiperInstance = null;

			if ( carousel ) {
				swiperInstance = window.GWSInitSwiperCarousel( carousel );
			}

			initModal( scopeEl, swiperInstance );
		} );
	}

	if ( window.elementorFrontend && window.elementorFrontend.hooks ) {
		registerElementReadyHook();
	} else {
		window.addEventListener( 'elementor/frontend/init', registerElementReadyHook );
	}
} )();
